import { useEffect, useRef, useState } from 'react';

interface UseFileDropZoneOptions {
  /** Called with the dropped files (already filtered by `acceptedMimeTypes`). */
  onFilesDropped: (files: File[]) => void;
  /** When true, dropped files are ignored. Used by callers to gate
   *  uploads behind eligibility flags (e.g. `caseStatus.canUploadDocuments`,
   *  access-control permissions). */
  disabled?: boolean;
  /** MIME types that pass through to `onFilesDropped`. Defaults to PDFs. */
  acceptedMimeTypes?: string[];
}

/**
 * Drop-anywhere hook. Subscribes to document-level `dragenter`/`dragleave`
 * so a drag from outside the window flips `isDragging` true, then mounts
 * `dragover`/`drop` listeners on the overlay element while the user is
 * still dragging. Dropping resets the counter and invokes
 * `onFilesDropped` with the MIME-filtered file list.
 *
 * Returns:
 *   - `overlayContainerRef` — attach to the visual drop-target element
 *     (typically a full-viewport `fixed inset-0` overlay rendered only
 *     when `isDragging` is true).
 *   - `isDragging` — drives whether the overlay should be visible.
 *   - `error` — non-null when the last drop included an invalid MIME
 *     type. Cleared on the next successful drop.
 */
export const useFileDropZone = ({
  onFilesDropped,
  disabled = false,
  acceptedMimeTypes = ['application/pdf'],
}: UseFileDropZoneOptions) => {
  const overlayContainerRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Document-level enter/leave counter. We add to it on each `dragenter`
  // and decrement on each `dragleave` because the browser fires those
  // events for every nested child the cursor crosses; tracking a counter
  // is the standard trick to detect "really left the document".
  useEffect(() => {
    if (disabled) return;
    const controller = new AbortController();
    const { signal } = controller;

    const handleDragEnter = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    document.addEventListener('dragenter', handleDragEnter, { signal });
    document.addEventListener('dragleave', handleDragLeave, { signal });

    return () => {
      controller.abort();
    };
  }, [disabled]);

  // Overlay-level handlers. Only active while `isDragging` is true so the
  // drop zone reacts to releases over the visible overlay; bound here
  // rather than at the document level so we can show drop-effect cursor
  // and a clear visual target.
  useEffect(() => {
    if (disabled) return;
    const controller = new AbortController();
    const { signal } = controller;

    const handleDragOver = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer!.dropEffect = 'copy';
    };

    const handleDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);

      if (!e.dataTransfer?.files) return;

      const newFiles: File[] = Array.from(e.dataTransfer.files);
      const allowedFiles = newFiles.filter((file) => acceptedMimeTypes.includes(file.type));
      const invalidFiles = newFiles.filter((file) => !acceptedMimeTypes.includes(file.type));

      if (invalidFiles.length > 0) {
        setError('Only PDF files allowed');
        return;
      }

      onFilesDropped(allowedFiles);
      setError(null);
    };

    const overlay = overlayContainerRef.current;
    if (overlay && isDragging) {
      overlay.addEventListener('dragover', handleDragOver, { signal });
      overlay.addEventListener('dragleave', handleDragLeave, { signal });
      overlay.addEventListener('drop', handleDrop, { signal });
    }

    return () => {
      controller.abort();
    };
  }, [isDragging, onFilesDropped, disabled, acceptedMimeTypes]);

  return { overlayContainerRef, isDragging, error };
};
